import { Component, createContext, useContext } from "react";

import type { ErrorInfo, ReactNode } from "react";

interface ErrorState {
  hasError: boolean;
  error: unknown;
}

interface Props {
  children: ReactNode;
  render: (props: { errorMessage: string; errorDetails?: string; closeError: () => void }) => ReactNode;
}

const ErrorContext = createContext<((error: unknown) => void) | undefined>(undefined);

function getErrorMessage(error: unknown): string {
  if (typeof error === "string") {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (error && typeof error === "object") {
    if ("responseText" in error && typeof error.responseText === "string" && error.responseText) {
      try {
        return getErrorMessage(JSON.parse(error.responseText));
      } catch (parseError) {
        if (!(parseError instanceof SyntaxError)) {
          throw parseError;
        }

        return error.responseText;
      }
    }

    for (const key of ["message", "Message", "detail", "title", "statusText"]) {
      if (key in error) {
        const message = Reflect.get(error, key);
        if (typeof message === "string" && message) {
          return message;
        }
      }
    }
  }

  return "Ett fel har uppst\u00E5tt. F\u00F6rs\u00F6k igen.";
}

export function useError() {
  const dispatchError = useContext(ErrorContext);
  if (!dispatchError) {
    throw new Error("useError must be used within an ErrorBoundary");
  }

  return dispatchError;
}

export class ErrorBoundary extends Component<Props, ErrorState> {
  state: ErrorState = { hasError: false, error: undefined };

  static getDerivedStateFromError(error: unknown): ErrorState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(error, errorInfo);
  }

  dispatchError = (error: unknown) => {
    console.error(error);
    this.setState({ hasError: true, error });
  };

  closeError = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    const { error, hasError } = this.state;

    return (
      <ErrorContext.Provider value={this.dispatchError}>
        {hasError
          ? this.props.render({
              errorMessage: getErrorMessage(error),
              errorDetails: import.meta.env.NODE_ENV === "development" && error instanceof Error ? error.stack : undefined,
              closeError: this.closeError
            })
          : this.props.children}
      </ErrorContext.Provider>
    );
  }
}
